import React, { useEffect, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { Redirect } from 'react-router-dom'

import SplashScreen from '@/components-latest/primitives/SplashScreen'
import ErrorState from '@/components-latest/shared/ErrorState'
import { PATHS } from '@/config/router/routes'
import { selectFlagsReady } from '@/ducks/app/selectors/app.selectors'
import { hasPermission, useAccessControl } from '@/ducks/auth/auth.permissions'
import { selectIsAuthed } from '@/ducks/auth/auth.selectors'
import { fetchMyRoles } from '@/ducks/auth/auth.shared'
import { PermissionRequirement } from '@/ducks/auth/auth.types'

import Styles from './ProtectedRoute.module.scss'

/**
 * How long a gated route may show the splash while it waits for the flags and
 * the permission set. Past this the user gets something they can act on — a
 * gated route must never wait forever, which is the whole point of EXSR-7567.
 */
const GATE_TIMEOUT_MS = 8000

interface ProtectedRouteProps {
  element: React.JSX.Element
  requiredPermission?: PermissionRequirement
}

const ProtectedRoute: React.FC<ProtectedRouteProps> = ({
  element,
  requiredPermission
}) => {
  const dispatch = useDispatch()
  const isAuthenticated = useSelector(selectIsAuthed)
  const flagsReady = useSelector(selectFlagsReady)
  const { isRbacEnabled, permissions, rolesLoaded, rolesError } =
    useAccessControl()

  const isGateResolved = flagsReady && (rolesLoaded || rolesError)
  const [hasGateTimedOut, setHasGateTimedOut] = useState(false)

  useEffect(() => {
    if (isGateResolved) {
      setHasGateTimedOut(false)
      return
    }

    // Depends on hasGateTimedOut so that clearing it (the retry button) arms a
    // fresh timer. Retrying while the original request is still pending changes
    // no redux state, so without this the timer is never rescheduled and the
    // route sits on the splash for good.
    if (hasGateTimedOut) {
      return
    }

    const timer = setTimeout(() => setHasGateTimedOut(true), GATE_TIMEOUT_MS)
    return () => clearTimeout(timer)
  }, [isGateResolved, hasGateTimedOut])

  if (!isAuthenticated) {
    // @ts-ignore
    return <Redirect to={PATHS.LOGIN} />
  }

  if (!requiredPermission) {
    return element
  }

  // Whether this route is gated at all depends on a LaunchDarkly flag. Render
  // the page only once that is known, otherwise it mounts, fires its queries
  // and is torn down again the moment RBAC flips on. If the flags never turn
  // up, fall through rather than block: with no flags RBAC is off, which is
  // exactly how the app behaved before flag-readiness was tracked.
  if (!flagsReady && !hasGateTimedOut) {
    return <SplashScreen />
  }

  if (!isRbacEnabled) {
    return element
  }

  // "We could not load your permissions" is not "you have none": redirecting
  // here is what dropped owners onto /orders with no explanation. A gate that
  // has run out of patience is treated the same way — visible, and retryable.
  if (!rolesLoaded && (rolesError || hasGateTimedOut)) {
    return (
      <div className={Styles.rolesError}>
        <ErrorState
          data-testid="roles-error"
          onRetry={() => {
            setHasGateTimedOut(false)
            dispatch(fetchMyRoles())
          }}
        />
      </div>
    )
  }

  if (!rolesLoaded) {
    return <SplashScreen />
  }

  return hasPermission(permissions, requiredPermission, { isRbacEnabled }) ? (
    element
  ) : (
    // @ts-ignore
    <Redirect to={PATHS.ORDERS} />
  )
}

export default ProtectedRoute
